<template>
  <el-dialog :visible.sync="dialogVisible" title="组件管理" width="80%" :before-close="handleClose">
    <div class="unit-manage-content">
      <!-- 组件列表 -->
      <div class="units-overview">
        <div class="overview-header">
          <h3>活动组件总览</h3>
          <div class="overview-controls">
            <el-input v-model="unitSearchText" placeholder="搜索组件..." size="small" prefix-icon="el-icon-search" clearable
              style="width: 200px;">
            </el-input>
            <el-select v-model="unitTypeFilter" placeholder="组件类型" size="small" clearable style="width: 160px;">
              <el-option label="全部" value=""></el-option>
              <el-option 
                v-for="item in UNIT_TYPES" 
                :key="item.value" 
                :label="item.label" 
                :value="item.value">
              </el-option>
            </el-select>
            <el-button size="small" @click="toggleViewMode"
              :icon="viewMode === 'grid' ? 'el-icon-menu' : 'el-icon-s-grid'">
              {{ viewMode === 'grid' ? '列表视图' : '网格视图' }}
            </el-button>
          </div>
        </div>

        <div class="units-stats">
          <el-alert
            :title="`共 ${filteredUnits.length} 个组件 ${eventData.units ? '(总计 ' + eventData.units.length + ' 个)' : ''}`"
            type="info" :closable="false" show-icon>
          </el-alert>
        </div>

        <!-- 网格视图 -->
        <div v-if="viewMode === 'grid'" class="units-grid">
          <div v-for="unit in paginatedUnits" :key="unit.unitId" class="unit-card"
            :class="{ 'selected': selectedUnit && selectedUnit.unitId === unit.unitId }" @click="selectUnit(unit)">
            <div class="unit-header">
              <span class="unit-type" :style="{ backgroundColor: getUnitTypeColor(unit.unitType) }">{{ getUnitTypeName(unit.unitType) }}</span>
              <span class="unit-id">#{{ unit.unitId }}</span>
            </div>
            <div class="unit-title">{{ unit.unitAka || unit.unitName }}</div>
            <div class="unit-desc">{{ unit.unitDesc || '暂无描述' }}</div>
            <div class="unit-weight">权重: {{ unit.weight || 0 }}</div>
            <div class="unit-actions">
              <el-button size="mini" type="success" icon="el-icon-copy-document" @click.stop="copyUnit(unit)">
                复制
              </el-button>
            </div>
          </div>
        </div>

        <!-- 列表视图 -->
        <div v-else class="units-list">
          <el-table :data="paginatedUnits" @row-click="selectUnit" :row-class-name="getRowClassName" stripe border
            height="300">
            <el-table-column label="组件ID" prop="unitId" width="80" align="center"></el-table-column>
            <el-table-column label="组件类型" width="100" align="center">
              <template slot-scope="{ row }">
                <el-tag :type="getUnitTypeTagType(row.unitType)" size="mini">
                  {{ getUnitTypeName(row.unitType) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="组件名称" prop="unitAka" align="center" show-overflow-tooltip>
              <template slot-scope="{ row }">
                {{ row.unitAka || row.unitName }}
              </template>
            </el-table-column>
            <el-table-column label="描述" prop="unitDesc" align="center" show-overflow-tooltip>
              <template slot-scope="{ row }">
                {{ row.unitDesc || '暂无描述' }}
              </template>
            </el-table-column>
            <el-table-column label="权重" prop="weight" width="80" align="center"></el-table-column>
            <el-table-column label="操作" width="140" align="center">
              <template slot-scope="{ row }">
                <el-button size="mini" type="primary" @click.stop="selectUnit(row)">
                  选择
                </el-button>
                <el-button size="mini" type="success" icon="el-icon-copy-document" @click.stop="copyUnit(row)">
                  复制
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 分页 -->
        <div v-if="filteredUnits.length > pageSize" class="units-pagination">
          <el-pagination background layout="prev, pager, next, total" :current-page="currentPage" :page-size="pageSize"
            :total="filteredUnits.length" @current-change="handlePageChange">
          </el-pagination>
        </div>
      </div>

      <!-- 选中组件的操作区域 -->
      <div v-if="selectedUnit" class="unit-operations">
        <h3>组件操作 - {{ selectedUnit.unitAka || selectedUnit.unitName }}</h3>

        <!-- 任务组件操作 -->
        <div v-if="selectedUnit.unitType === 3" class="task-operations">
          <el-alert title="任务组件操作" type="info" :closable="false" show-icon></el-alert>
          <div class="operation-form">
            <el-form :inline="true">
              <el-form-item label="用户RID:">
                <el-input v-model="taskUserId" placeholder="请输入用户RID"></el-input>
              </el-form-item>
              <el-form-item>
                <el-button type="primary" @click="refreshUserTask">刷新用户任务</el-button>
              </el-form-item>
            </el-form>
            <p class="operation-desc">刷新指定用户的任务进度和状态</p>
          </div>

          <!-- 任务组件详细信息 -->
          <div class="task-details">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="任务类型">{{ getTaskTypeName(selectedUnit.taskUnit?.type)
                }}</el-descriptions-item>
              <el-descriptions-item label="任务模式">{{ getTaskModeName(selectedUnit.taskUnit?.mode) }} ({{
                selectedUnit.taskUnit?.mode }})</el-descriptions-item>
              <el-descriptions-item label="任务周期">{{ getTaskDurationName(selectedUnit.taskUnit?.duration)
                }}</el-descriptions-item>
              <el-descriptions-item label="自动发奖">{{ selectedUnit.taskUnit?.autoGiveAward ? '是' : '否'
                }}</el-descriptions-item>
              <el-descriptions-item label="检查参数数量">{{ selectedUnit.taskUnit?.checkParamsSet?.length || 0 }}
                个</el-descriptions-item>
              <el-descriptions-item label="任务配置数量">{{ selectedUnit.taskUnit?.configs?.length || 0 }}
                个</el-descriptions-item>
              <el-descriptions-item label="是否为队伍任务">
                <el-tag :type="selectedUnit.taskUnit?.teamTask ? 'success' : 'info'" size="mini">
                  {{ selectedUnit.taskUnit?.teamTask ? '是' : '否' }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="队伍组件ID" v-if="selectedUnit.taskUnit?.teamTask">
                {{ selectedUnit.taskUnit?.teamUnitId || '-' }}
              </el-descriptions-item>
            </el-descriptions>

            <!-- 检查参数详细列表 -->
            <div v-if="selectedUnit.taskUnit?.checkParamsSet?.length > 0" class="detail-section">
              <h5>检查参数详细 ({{ selectedUnit.taskUnit.checkParamsSet.length }} 个):</h5>
              <el-tag v-for="param in selectedUnit.taskUnit.checkParamsSet" :key="param"
                style="margin-right: 8px; margin-bottom: 4px;">
                {{ param }}
              </el-tag>
            </div>

            <!-- 单节点配置：当mode不是2时显示 -->
            <div v-if="selectedUnit.taskUnit?.config && selectedUnit.taskUnit?.mode !== 2" class="detail-section">
              <h5>单节点任务配置:</h5>
              <el-descriptions :column="2" border size="small">
                <el-descriptions-item label="检查参数">{{ selectedUnit.taskUnit.config.checkParams }}</el-descriptions-item>
                <el-descriptions-item label="显示倍率">{{ selectedUnit.taskUnit.config.showRate }}</el-descriptions-item>
                <el-descriptions-item label="检查文本">{{ selectedUnit.taskUnit.config.checkText || '-'
                  }}</el-descriptions-item>
                <el-descriptions-item label="限制次数">{{ selectedUnit.taskUnit.config.limit === -1 ? '无限制' :
                  selectedUnit.taskUnit.config.limit }}</el-descriptions-item>
                <el-descriptions-item label="奖励Key">{{ selectedUnit.taskUnit.config.awardsKey || '-'
                  }}</el-descriptions-item>
                <el-descriptions-item label="自动发送通知">{{ selectedUnit.taskUnit.config.autoSendNotice ? '是' : '否'
                  }}</el-descriptions-item>
                <el-descriptions-item label="通知消息" :span="2">{{ selectedUnit.taskUnit.config.notice || '-'
                  }}</el-descriptions-item>
              </el-descriptions>
            </div>

            <!-- 多节点配置：当mode为2时显示 -->
            <div v-else-if="selectedUnit.taskUnit?.configs?.length > 0 && selectedUnit.taskUnit?.mode === 2"
              class="detail-section">
              <h5>多节点任务配置 ({{ selectedUnit.taskUnit.configs.length }} 个):</h5>
              <el-table :data="selectedUnit.taskUnit.configs" border size="small">
                <el-table-column prop="checkParams" label="检查参数" width="100"></el-table-column>
                <el-table-column prop="showRate" label="显示倍率" width="100"></el-table-column>
                <el-table-column prop="checkText" label="检查文本" width="150" show-overflow-tooltip></el-table-column>
                <el-table-column prop="limit" label="限制次数" width="100">
                  <template slot-scope="{ row }">
                    {{ row.limit === -1 ? '无限制' : row.limit }}
                  </template>
                </el-table-column>
                <el-table-column prop="awardsKey" label="奖励Key" width="150" show-overflow-tooltip></el-table-column>
                <el-table-column prop="autoSendNotice" label="自动通知" width="100">
                  <template slot-scope="{ row }">
                    <el-tag :type="row.autoSendNotice ? 'success' : 'info'" size="mini">
                      {{ row.autoSendNotice ? '是' : '否' }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column prop="notice" label="通知消息" show-overflow-tooltip></el-table-column>
              </el-table>
            </div>

            <!-- 调试信息：显示当前mode值和配置情况 -->
            <div class="detail-section"
              style="background-color: #f5f5f5; padding: 8px; border-radius: 4px; margin-top: 10px;">
              <h6 style="margin: 0 0 8px 0; color: #666;">调试信息:</h6>
              <p style="margin: 4px 0; font-size: 12px;">Mode值: {{ selectedUnit.taskUnit?.mode }}</p>
              <p style="margin: 4px 0; font-size: 12px;">是否有config: {{ !!selectedUnit.taskUnit?.config }}</p>
              <p style="margin: 4px 0; font-size: 12px;">configs数量: {{ selectedUnit.taskUnit?.configs?.length || 0 }}
              </p>
            </div>
          </div>
        </div>

        <!-- 奖池组件操作 -->
        <div v-if="selectedUnit.unitType === 2" class="pool-operations">
          <el-alert title="奖池组件操作" type="warning" :closable="false" show-icon></el-alert>
          <div class="operation-form">
            <el-form :inline="true">
              <el-form-item label="奖池类型:">
                <el-select v-model="poolType" placeholder="请选择奖池类型">
                  <el-option label="全部奖池" value="-1"></el-option>
                  <el-option label="普通奖池" value="0"></el-option>
                  <el-option label="三方充值分奖池" value="1"></el-option>
                </el-select>
              </el-form-item>
              <el-form-item>
                <el-button type="warning" @click="refreshAwardPool">刷新奖池</el-button>
              </el-form-item>
            </el-form>
            <p class="operation-desc">重新生成奖池内容，会影响所有用户的抽奖结果</p>
          </div>

          <!-- 奖池组件详细信息 -->
          <div class="pool-details">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="次数汇率">{{ selectedUnit.awardPoolUnit?.timesRate || 1 }}</el-descriptions-item>
              <el-descriptions-item label="奖励Key">{{ selectedUnit.awardPoolUnit?.awardsKey || '-'
                }}</el-descriptions-item>
              <el-descriptions-item label="消耗货币组件">{{ (selectedUnit.awardPoolUnit?.costUnitIds ||
                selectedUnit.awardPoolUnit?.costUnitId)?.length || 0 }} 个</el-descriptions-item>
              <el-descriptions-item label="开启三方充值分奖池">
                <el-tag :type="selectedUnit.awardPoolUnit?.enablePayPool ? 'success' : 'info'" size="mini">
                  {{ selectedUnit.awardPoolUnit?.enablePayPool ? '是' : '否' }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="近30天最小充值美金数" v-if="selectedUnit.awardPoolUnit?.enablePayPool">
                {{ selectedUnit.awardPoolUnit?.minPayUsd || 0 }} 美金
              </el-descriptions-item>
            </el-descriptions>

            <!-- 三方充值分奖池说明 -->
            <div v-if="selectedUnit.awardPoolUnit?.enablePayPool" class="detail-section">
              <el-alert
                title="开启后，普通奖池使用【概率】，三方充值奖池使用【概率1】"
                type="info"
                :closable="false"
                show-icon>
              </el-alert>
            </div>

            <!-- 消耗货币组件列表 -->
            <div v-if="(selectedUnit.awardPoolUnit?.costUnitIds || selectedUnit.awardPoolUnit?.costUnitId)?.length > 0"
              class="detail-section">
              <h5>消耗货币组件:</h5>
              <el-tag
                v-for="unitId in (selectedUnit.awardPoolUnit.costUnitIds || selectedUnit.awardPoolUnit.costUnitId)"
                :key="unitId" style="margin-right: 8px; margin-bottom: 4px;">
                组件ID: {{ unitId }}
              </el-tag>
            </div>
          </div>
        </div>

        <!-- 步频奖池组件信息 -->
        <div v-if="selectedUnit.unitType === 5" class="step-pool-info">
          <el-alert title="步频奖池组件信息" type="info" :closable="false" show-icon></el-alert>
          <div class="step-pool-details">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="最大步频">{{ selectedUnit.stepPoolUnit?.maxSteps || 0 }}</el-descriptions-item>
              <el-descriptions-item label="刷新消耗">{{ selectedUnit.stepPoolUnit?.refreshCost || 0
                }}</el-descriptions-item>
              <el-descriptions-item label="消耗货币组件">{{ selectedUnit.stepPoolUnit?.costUnitIds?.length || 0 }}
                个</el-descriptions-item>
              <el-descriptions-item label="子奖池数量">{{ selectedUnit.stepPoolUnit?.sonPools?.length || 0 }}
                个</el-descriptions-item>
              <el-descriptions-item label="步频配置数量">{{ selectedUnit.stepPoolUnit?.stepConfigs?.length || 0 }}
                个</el-descriptions-item>
              <el-descriptions-item label="步频奖励数量">{{ selectedUnit.stepPoolUnit?.stepRewards?.length || 0 }}
                个</el-descriptions-item>
            </el-descriptions>

            <!-- 子奖池详情 -->
            <div v-if="selectedUnit.stepPoolUnit?.sonPools?.length > 0" class="detail-section">
              <h5>子奖池配置:</h5>
              <el-table :data="selectedUnit.stepPoolUnit.sonPools" border size="small">
                <el-table-column prop="poolId" label="奖池ID" width="120"></el-table-column>
                <el-table-column prop="poolName" label="奖池名称" width="150"></el-table-column>
                <el-table-column prop="awardsKey" label="奖励Key" width="200"></el-table-column>
              </el-table>
            </div>

            <!-- 步频配置详情 -->
            <div v-if="selectedUnit.stepPoolUnit?.stepConfigs?.length > 0" class="detail-section">
              <h5>步频配置:</h5>
              <el-table :data="selectedUnit.stepPoolUnit.stepConfigs" border size="small">
                <el-table-column prop="step" label="步频序号" width="100"></el-table-column>
                <el-table-column prop="poolId" label="对应奖池ID" width="120"></el-table-column>
                <el-table-column prop="cost" label="消耗数量" width="100"></el-table-column>
                <el-table-column prop="remainingCost" label="剩余消耗" width="100"></el-table-column>
              </el-table>
            </div>

            <!-- 步频奖励详情 -->
            <div v-if="selectedUnit.stepPoolUnit?.stepRewards?.length > 0" class="detail-section">
              <h5>步频奖励配置:</h5>
              <el-table :data="selectedUnit.stepPoolUnit.stepRewards" border size="small">
                <el-table-column prop="step" label="触发步频" width="100"></el-table-column>
                <el-table-column prop="awardsKey" label="奖励Key" width="200"></el-table-column>
                <el-table-column prop="notice" label="通知消息" show-overflow-tooltip></el-table-column>
              </el-table>
            </div>
          </div>
        </div>

        <!-- 榜单组件操作 -->
        <div v-if="selectedUnit.unitType === 4" class="rank-operations">
          <el-alert title="榜单组件操作" type="success" :closable="false" show-icon></el-alert>
          <div class="operation-form">
            <p class="operation-desc">榜单相关操作请使用专门的榜单管理功能</p>
            <el-button type="success" @click="openRankManage">打开榜单管理</el-button>
          </div>

          <!-- 排行组件详细信息 -->
          <div class="rank-details">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="排行类型">{{ getRankTypeName(selectedUnit.rankUnit?.rankType)
                }}</el-descriptions-item>
              <el-descriptions-item label="显示前N名">{{ selectedUnit.rankUnit?.showTop || 10 }}</el-descriptions-item>
              <el-descriptions-item label="奖励前N名">{{ selectedUnit.rankUnit?.awardTop || 10 }}</el-descriptions-item>
              <el-descriptions-item label="积分倍率">{{ selectedUnit.rankUnit?.scoreRate || 1 }}</el-descriptions-item>
              <el-descriptions-item label="自动发奖">
                <el-tag :type="selectedUnit.rankUnit?.autoGiveAward ? 'success' : 'info'" size="mini">
                  {{ selectedUnit.rankUnit?.autoGiveAward ? '是' : '否' }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="自动发送全局通知">
                <el-tag :type="selectedUnit.rankUnit?.autoSendGlobalNotice ? 'success' : 'info'" size="mini">
                  {{ selectedUnit.rankUnit?.autoSendGlobalNotice ? '是' : '否' }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="开始时间">{{ formatTimestamp(selectedUnit.rankUnit?.startTime)
                }}</el-descriptions-item>
              <el-descriptions-item label="结束时间">{{ formatTimestamp(selectedUnit.rankUnit?.endTime)
                }}</el-descriptions-item>
              <el-descriptions-item label="官方消息支持渠道" :span="2">
                <div v-if="selectedUnit.rankUnit?.noticeSupportChannel?.length > 0">
                  <el-tag 
                    v-for="channel in selectedUnit.rankUnit.noticeSupportChannel" 
                    :key="channel" 
                    size="small"
                    style="margin-right: 8px; margin-bottom: 4px;">
                    {{ getChannelName(channel) }}
                  </el-tag>
                </div>
                <span v-else style="color: #909399;">不限制渠道</span>
              </el-descriptions-item>
              <el-descriptions-item label="检查参数数量">{{ selectedUnit.rankUnit?.checkParamsSet?.length || 0 }}
                个</el-descriptions-item>
              <el-descriptions-item label="奖励配置数量">{{ selectedUnit.rankUnit?.rewardConfigs?.length || 0 }}
                个</el-descriptions-item>
              <!-- 新增字段 -->
              <el-descriptions-item label="开启实时榜单标签">{{ selectedUnit.rankUnit?.enableRankLabel ? '是' : '否'
                }}</el-descriptions-item>
              <el-descriptions-item label="榜单周期类型">{{ getRankDurationName(selectedUnit.rankUnit?.rankDuration)
                }}</el-descriptions-item>
              <el-descriptions-item label="开启榜单总数据分成奖池">{{ selectedUnit.rankUnit?.enablePool ? '是' : '否'
                }}</el-descriptions-item>
              <el-descriptions-item label="实时榜单标签配置数量">{{ selectedUnit.rankUnit?.rankLabelConfigs?.length || 0 }}
                个</el-descriptions-item>
              <el-descriptions-item label="榜单奖池分成配置数量">{{ selectedUnit.rankUnit?.rankPoolInfo?.rankPoolConfigs?.length
                || 0 }}
                个</el-descriptions-item>
              <el-descriptions-item label="是否为队伍榜单">
                <el-tag :type="selectedUnit.rankUnit?.teamRank ? 'success' : 'info'" size="mini">
                  {{ selectedUnit.rankUnit?.teamRank ? '是' : '否' }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="队伍组件ID" v-if="selectedUnit.rankUnit?.teamRank">
                {{ selectedUnit.rankUnit?.teamUnitId || '-' }}
              </el-descriptions-item>
              <el-descriptions-item label="开启子榜单">
                <el-tag :type="selectedUnit.rankUnit?.enableSonRank ? 'success' : 'info'" size="mini">
                  {{ selectedUnit.rankUnit?.enableSonRank ? '是' : '否' }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="子榜单配置" v-if="selectedUnit.rankUnit?.enableSonRank">
                展示TOP {{ selectedUnit.rankUnit?.sonRankConfig?.showTop || 0 }} / 奖励TOP {{ selectedUnit.rankUnit?.sonRankConfig?.awardTop || 0 }}
              </el-descriptions-item>
            </el-descriptions>

            <!-- 子榜单详细配置 -->
            <div v-if="selectedUnit.rankUnit?.enableSonRank && selectedUnit.rankUnit?.sonRankConfig" class="detail-section">
              <h5>子榜单配置详情:</h5>
              <el-descriptions :column="2" border size="small">
                <el-descriptions-item label="展示的子榜单TOP数量">{{ selectedUnit.rankUnit.sonRankConfig.showTop || 0 }}</el-descriptions-item>
                <el-descriptions-item label="奖励的子榜单TOP数量">{{ selectedUnit.rankUnit.sonRankConfig.awardTop || 0 }}</el-descriptions-item>
                <el-descriptions-item label="限制只能异性上榜">
                  <el-tag :type="selectedUnit.rankUnit.sonRankConfig.diffSex ? 'warning' : 'info'" size="mini">
                    {{ selectedUnit.rankUnit.sonRankConfig.diffSex ? '是' : '否' }}
                  </el-tag>
                </el-descriptions-item>
              </el-descriptions>
              <el-alert
                title="只有收礼相关榜单可以开启子榜单，其他类型榜单配置无效"
                type="info"
                :closable="false"
                show-icon
                style="margin-top: 8px;">
              </el-alert>
            </div>

            <!-- 积分图标 -->
            <div v-if="selectedUnit.rankUnit?.scoreIcon" class="detail-section">
              <h5>积分图标:</h5>
              <div class="score-icon-display">
                <div class="icon-preview">
                  <img v-if="selectedUnit.rankUnit.scoreIcon" :src="selectedUnit.rankUnit.scoreIcon" :alt="'积分图标'"
                    class="score-icon-img" @error="handleImageError" @load="handleImageLoad" />
                  <div v-else class="no-image">无图片</div>
                </div>
                <div class="icon-link">
                  <el-link :href="selectedUnit.rankUnit.scoreIcon" target="_blank" type="primary">
                    <i class="el-icon-link"></i> 查看原图
                  </el-link>
                </div>
              </div>
            </div>

            <!-- 检查参数列表 -->
            <div v-if="selectedUnit.rankUnit?.checkParamsSet?.length > 0" class="detail-section">
              <h5>检查参数 ({{ selectedUnit.rankUnit.checkParamsSet.length }} 个):</h5>
              <el-tag v-for="param in selectedUnit.rankUnit.checkParamsSet" :key="param"
                style="margin-right: 8px; margin-bottom: 4px;">
                {{ param }}
              </el-tag>
            </div>

            <!-- 奖励配置 -->
            <div v-if="selectedUnit.rankUnit?.rewardConfigs?.length > 0" class="detail-section">
              <h5>奖励配置 ({{ selectedUnit.rankUnit.rewardConfigs.length }} 个):</h5>
              <el-table :data="selectedUnit.rankUnit.rewardConfigs" border size="small">
                <el-table-column label="排名范围" width="120">
                  <template slot-scope="{ row, $index }">
                    {{ formatRankRange(selectedUnit.rankUnit.rewardConfigs, $index) }}
                  </template>
                </el-table-column>
                <el-table-column prop="awardsKey" label="奖励Key" show-overflow-tooltip></el-table-column>
                <el-table-column prop="sonRankAwardsKey" label="子榜单奖励Key" show-overflow-tooltip v-if="selectedUnit.rankUnit?.enableSonRank">
                  <template slot-scope="{ row }">
                    <span v-if="row.sonRankAwardsKey">{{ row.sonRankAwardsKey }}</span>
                    <span v-else style="color: #c0c4cc;">-</span>
                  </template>
                </el-table-column>
              </el-table>
            </div>

            <!-- 新增：实时榜单标签配置 -->
            <div v-if="selectedUnit.rankUnit?.enableRankLabel && selectedUnit.rankUnit?.rankLabelConfigs?.length > 0"
              class="detail-section">
              <h5>实时榜单标签配置 ({{ selectedUnit.rankUnit.rankLabelConfigs.length }} 个):</h5>
              <el-table :data="selectedUnit.rankUnit.rankLabelConfigs" border size="small">
                <el-table-column prop="maxTop" label="最大榜单名次" width="120"></el-table-column>
                <el-table-column prop="weight" label="权重" width="100"></el-table-column>
                <el-table-column prop="labelImg" label="标签图片" show-overflow-tooltip>
                  <template slot-scope="{ row }">
                    <el-link v-if="row.labelImg" :href="row.labelImg" target="_blank">{{ row.labelImg }}</el-link>
                    <span v-else>-</span>
                  </template>
                </el-table-column>
              </el-table>
            </div>

            <!-- 新增：榜单奖池配置 -->
            <div v-if="selectedUnit.rankUnit?.enablePool && selectedUnit.rankUnit?.rankPoolInfo" class="detail-section">
              <h5>榜单奖池配置:</h5>
              <el-descriptions :column="2" border size="small">
                <el-descriptions-item label="榜单数值万分率">{{ ((selectedUnit.rankUnit.rankPoolInfo.poolRate || 0) / 100).toFixed(2)
                  }}%</el-descriptions-item>
                <el-descriptions-item label="基础奖池金额">{{ selectedUnit.rankUnit.rankPoolInfo.basePoolAmount || '0' }}
                  后台金币</el-descriptions-item>
                <el-descriptions-item label="奖池金额上限">{{ selectedUnit.rankUnit.rankPoolInfo.maxPoolAmount || '0' }}
                  后台金币</el-descriptions-item>
                <el-descriptions-item label="奖池金额展示倍率">{{ selectedUnit.rankUnit.rankPoolInfo.showRate || 0
                  }}</el-descriptions-item>
              </el-descriptions>

              <!-- 榜单奖池分成配置 -->
              <div v-if="selectedUnit.rankUnit.rankPoolInfo.rankPoolConfigs?.length > 0" style="margin-top: 12px;">
                <h6>榜单奖池分成配置 ({{ selectedUnit.rankUnit.rankPoolInfo.rankPoolConfigs.length }} 个):</h6>
                <el-table :data="selectedUnit.rankUnit.rankPoolInfo.rankPoolConfigs" border size="small">
                  <el-table-column label="排名范围" width="120">
                    <template slot-scope="{ row, $index }">
                      {{ formatRankRange(selectedUnit.rankUnit.rankPoolInfo.rankPoolConfigs, $index) }}
                    </template>
                  </el-table-column>
                  <el-table-column prop="minScore" label="最小积分数" width="120"></el-table-column>
                  <el-table-column prop="rate" label="万分率" width="100"></el-table-column>
                </el-table>
              </div>
            </div>
          </div>
        </div>

        <!-- 货币组件信息 -->
        <div v-if="selectedUnit.unitType === 1" class="currency-info">
          <el-alert title="货币组件信息" type="primary" :closable="false" show-icon></el-alert>
          <div class="currency-details">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="货币类型">{{ getCurrencyTypeName(selectedUnit.currencyUnit?.type)
                }}</el-descriptions-item>
              <el-descriptions-item label="显示倍率">{{ selectedUnit.currencyUnit?.showRate || 1 }}</el-descriptions-item>
              <el-descriptions-item label="开启自动抽奖">
                <el-tag :type="selectedUnit.currencyUnit?.enableAutoDraw ? 'success' : 'info'" size="mini">
                  {{ selectedUnit.currencyUnit?.enableAutoDraw ? '是' : '否' }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="奖池组件ID" v-if="selectedUnit.currencyUnit?.enableAutoDraw">
                {{ selectedUnit.currencyUnit?.awardPoolId || '-' }}
              </el-descriptions-item>
              <el-descriptions-item label="开启货币赠送">
                <el-tag :type="selectedUnit.currencyUnit?.enableGive ? 'success' : 'info'" size="mini">
                  {{ selectedUnit.currencyUnit?.enableGive ? '是' : '否' }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="单次赠送上限" v-if="selectedUnit.currencyUnit?.enableGive">
                {{ selectedUnit.currencyUnit?.giveConfig?.onceMax === -1 ? '不限制' :
                  (selectedUnit.currencyUnit?.giveConfig?.onceMax || 0) }}
              </el-descriptions-item>
              <el-descriptions-item label="图标URL" :span="2">
                <div class="icon-url-display">
                  <div class="icon-preview">
                    <img 
                      v-if="selectedUnit.currencyUnit?.icon" 
                      :src="selectedUnit.currencyUnit?.icon" 
                      class="currency-icon"
                      @error="handleImageError"
                      alt="货币图标"
                    />
                    <div v-else class="no-icon">
                      <i class="el-icon-picture-outline"></i>
                      <span>暂无图标</span>
                    </div>
                  </div>
                  <div class="icon-link">
                    <el-link 
                      v-if="selectedUnit.currencyUnit?.icon" 
                      :href="selectedUnit.currencyUnit?.icon" 
                      target="_blank"
                      type="primary"
                      :underline="false"
                    >
                      <i class="el-icon-link"></i>
                      查看原图
                    </el-link>
                    <span v-else class="no-url">-</span>
                  </div>
                </div>
              </el-descriptions-item>
            </el-descriptions>
          </div>
        </div>

        <!-- 商店组件信息 -->
        <div v-if="selectedUnit.unitType === 6" class="shop-info">
          <el-alert title="商店组件信息" type="info" :closable="false" show-icon></el-alert>
          <div class="shop-details">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="消耗货币组件">{{ selectedUnit.shopUnit?.costUnitIds?.length || 0 }}
                个</el-descriptions-item>
              <el-descriptions-item label="限制条件使用周期">{{ getTaskDurationName(selectedUnit.shopUnit?.duration) }}</el-descriptions-item>
              <el-descriptions-item label="商品数量">{{ selectedUnit.shopUnit?.items?.length || 0 }}
                个</el-descriptions-item>
            </el-descriptions>

            <!-- 消耗货币组件列表 -->
            <div v-if="selectedUnit.shopUnit?.costUnitIds?.length > 0" class="detail-section">
              <h5>消耗货币组件:</h5>
              <el-tag v-for="unitId in selectedUnit.shopUnit.costUnitIds" :key="unitId"
                style="margin-right: 8px; margin-bottom: 4px;">
                组件ID: {{ unitId }}
              </el-tag>
            </div>

            <!-- 商品列表 -->
            <div v-if="selectedUnit.shopUnit?.items?.length > 0" class="detail-section">
              <h5>商品列表 ({{ selectedUnit.shopUnit.items.length }} 个):</h5>
              <el-table :data="selectedUnit.shopUnit.items" border size="small">
                <el-table-column prop="awardsKey" label="奖励Key" width="200" show-overflow-tooltip></el-table-column>
                <el-table-column prop="showPrice" label="显示价格" width="120"></el-table-column>
                <el-table-column prop="price" label="实际价格" width="120"></el-table-column>
                <el-table-column prop="weight" label="权重" width="100"></el-table-column>
                <el-table-column prop="globalLimit" label="全局限购数量" width="120">
                  <template slot-scope="{ row }">
                    {{ row.globalLimit === -1 ? '不限制' : (row.globalLimit === 0 ? '已售罄' : row.globalLimit) }}
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </div>

        <!-- 货币合成组件信息 -->
        <div v-if="selectedUnit.unitType === 7" class="currency-merge-info">
          <el-alert title="货币合成组件信息" type="info" :closable="false" show-icon></el-alert>
          <div class="currency-merge-details">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="消耗货币组件">{{ selectedUnit.currencyMergeUnit?.costCurrencyUnitIds?.length || 0
                }}
                个</el-descriptions-item>
              <el-descriptions-item label="周期">{{ getTaskDurationName(selectedUnit.currencyMergeUnit?.duration) }}</el-descriptions-item>
              <el-descriptions-item label="周期内最大合成次数">
                {{ selectedUnit.currencyMergeUnit?.limit === -1 ? '不限制' : (selectedUnit.currencyMergeUnit?.limit || 0) }}
              </el-descriptions-item>
              <el-descriptions-item label="奖励Key">{{ selectedUnit.currencyMergeUnit?.awardsKey || '-'
                }}</el-descriptions-item>
            </el-descriptions>

            <!-- 消耗货币组件列表 -->
            <div v-if="selectedUnit.currencyMergeUnit?.costCurrencyUnitIds?.length > 0" class="detail-section">
              <h5>消耗货币组件:</h5>
              <el-tag v-for="unitId in selectedUnit.currencyMergeUnit.costCurrencyUnitIds" :key="unitId"
                style="margin-right: 8px; margin-bottom: 4px;">
                组件ID: {{ unitId }}
              </el-tag>
            </div>
          </div>
        </div>

        <!-- 分享组件信息 -->
        <div v-if="selectedUnit.unitType === 9" class="share-info">
          <el-alert title="分享奖励组件信息" type="success" :closable="false" show-icon></el-alert>
          <div class="share-details">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="消耗货币组件">{{ selectedUnit.shareUnit?.costUnitIds?.length || 0 }}
                个</el-descriptions-item>
              <el-descriptions-item label="分享一次消耗数量">{{ selectedUnit.shareUnit?.needCost || 0
                }}</el-descriptions-item>
              <el-descriptions-item label="分享者奖励Key">{{ selectedUnit.shareUnit?.shareAwardsKey || '-'
                }}</el-descriptions-item>
              <el-descriptions-item label="被分享者奖励Key">{{ selectedUnit.shareUnit?.shareToAwardsKey || '-'
                }}</el-descriptions-item>
              <el-descriptions-item label="被分享者通知文案" :span="2">
                <div class="notice-message">
                  {{ selectedUnit.shareUnit?.shareToNotice || '未设置' }}
                </div>
              </el-descriptions-item>
            </el-descriptions>

            <!-- 消耗货币组件列表 -->
            <div v-if="selectedUnit.shareUnit?.costUnitIds?.length > 0" class="detail-section">
              <h5>消耗货币组件:</h5>
              <el-tag v-for="unitId in selectedUnit.shareUnit.costUnitIds" :key="unitId"
                style="margin-right: 8px; margin-bottom: 4px;">
                组件ID: {{ unitId }}
              </el-tag>
            </div>
          </div>
        </div>

        <!-- 刮刮乐组件信息 -->
        <div v-if="selectedUnit.unitType === 10" class="scratch-card-info">
          <el-alert title="刮刮乐组件信息" type="info" :closable="false" show-icon></el-alert>
          <div class="scratch-card-details">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="奖池组件ID">{{ selectedUnit.scratchCardUnit?.poolUnitId || '-' }}</el-descriptions-item>
              <el-descriptions-item label="其他数字奖池组件ID">{{ selectedUnit.scratchCardUnit?.lowPoolUnitId || '-' }}</el-descriptions-item>
              <el-descriptions-item label="其他数字随机数最小值">{{ selectedUnit.scratchCardUnit?.randomMin || 0 }}</el-descriptions-item>
              <el-descriptions-item label="其他数字随机数最大值">{{ selectedUnit.scratchCardUnit?.randomMax || 0 }}</el-descriptions-item>
              <el-descriptions-item label="奖励映射数量">{{ selectedUnit.scratchCardUnit?.mappingList?.length || 0 }} 个</el-descriptions-item>
            </el-descriptions>

            <!-- 奖励映射列表 -->
            <div v-if="selectedUnit.scratchCardUnit?.mappingList?.length > 0" class="detail-section">
              <h5>奖励映射列表 ({{ selectedUnit.scratchCardUnit.mappingList.length }} 个):</h5>
              <el-table :data="selectedUnit.scratchCardUnit.mappingList" border size="small">
                <el-table-column prop="unitId" label="货币组件ID" width="150">
                  <template slot-scope="{ row }">
                    {{ row.unitId || '-' }}
                  </template>
                </el-table-column>
                <el-table-column prop="awardsKey" label="奖励Key" show-overflow-tooltip></el-table-column>
              </el-table>
            </div>
          </div>
        </div>

        <!-- 队伍组件信息 -->
        <div v-if="selectedUnit.unitType === 8" class="team-info">
          <el-alert title="队伍组件信息" type="primary" :closable="false" show-icon></el-alert>
          <div class="team-details">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="最大人数">{{ selectedUnit.teamUnit?.maxMember || 0 }} 人</el-descriptions-item>
              <el-descriptions-item label="队伍周期">{{ getTaskDurationName(selectedUnit.teamUnit?.duration) }}</el-descriptions-item>
              <el-descriptions-item label="入队通知消息" :span="2">
                <div class="notice-message">
                  {{ selectedUnit.teamUnit?.memberNotice || '未设置' }}
                </div>
              </el-descriptions-item>
              <el-descriptions-item label="队长通知消息" :span="2">
                <div class="notice-message">
                  {{ selectedUnit.teamUnit?.captainNotice || '未设置' }}
                </div>
              </el-descriptions-item>
            </el-descriptions>

            <!-- 队伍信息 -->
            <div v-if="selectedUnit.teamInfo" class="detail-section">
              <h5>当前队伍信息:</h5>
              <el-descriptions :column="2" border size="small">
                <el-descriptions-item label="队长UID">{{ selectedUnit.teamInfo.captainUid || '-' }}</el-descriptions-item>
                <el-descriptions-item label="队伍名称">{{ selectedUnit.teamInfo.teamName || '-' }}</el-descriptions-item>
                <el-descriptions-item label="队伍头像" :span="2">
                  <div class="team-avatar-display">
                    <div class="avatar-preview">
                      <img 
                        v-if="selectedUnit.teamInfo.head" 
                        :src="selectedUnit.teamInfo.head" 
                        class="team-avatar"
                        @error="handleImageError"
                        alt="队伍头像"
                      />
                      <div v-else class="no-avatar">
                        <i class="el-icon-user"></i>
                        <span>暂无头像</span>
                      </div>
                    </div>
                    <div class="avatar-link">
                      <el-link 
                        v-if="selectedUnit.teamInfo.head" 
                        :href="selectedUnit.teamInfo.head" 
                        target="_blank"
                        type="primary"
                        :underline="false"
                      >
                        <i class="el-icon-link"></i>
                        查看原图
                      </el-link>
                      <span v-else class="no-url">-</span>
                    </div>
                  </div>
                </el-descriptions-item>
              </el-descriptions>

              <!-- 队员列表 -->
              <div v-if="selectedUnit.teamInfo.teamMembers?.length > 0" style="margin-top: 12px;">
                <h6>队员列表 ({{ selectedUnit.teamInfo.teamMembers.length }} 人):</h6>
                <el-table :data="selectedUnit.teamInfo.teamMembers" border size="small">
                  <el-table-column prop="uid" label="UID" width="120" show-overflow-tooltip></el-table-column>
                  <el-table-column prop="rid" label="RID" width="100"></el-table-column>
                  <el-table-column prop="name" label="姓名" width="120" show-overflow-tooltip></el-table-column>
                  <el-table-column prop="head" label="头像" width="80">
                    <template slot-scope="{ row }">
                      <img 
                        v-if="row.head" 
                        :src="row.head" 
                        class="member-avatar"
                        @error="handleImageError"
                        alt="头像"
                      />
                      <span v-else>-</span>
                    </template>
                  </el-table-column>
                  <el-table-column prop="inviteTime" label="邀请时间" width="150">
                    <template slot-scope="{ row }">
                      {{ formatTimestamp(row.inviteTime) }}
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </div>
          </div>
        </div>

        <!-- 通用组件信息 -->
        <div class="unit-details">
          <h4>组件详细信息:</h4>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="组件ID">{{ selectedUnit.unitId }}</el-descriptions-item>
            <el-descriptions-item label="组件类型">{{ getUnitTypeName(selectedUnit.unitType) }}</el-descriptions-item>
            <el-descriptions-item label="组件名称">{{ selectedUnit.unitName }}</el-descriptions-item>
            <el-descriptions-item label="组件别名">{{ selectedUnit.unitAka }}</el-descriptions-item>
            <el-descriptions-item label="权重">{{ selectedUnit.weight }}</el-descriptions-item>
            <el-descriptions-item label="描述" :span="2">{{ selectedUnit.unitDesc }}</el-descriptions-item>
          </el-descriptions>
        </div>
      </div>

      <div v-else class="no-selection">
        <el-empty description="请选择一个组件进行操作"></el-empty>
      </div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">关闭</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { refreshUnitAwardPoolReq, refreshUnitTaskReq } from '@/api/activityUnitRequest'
import { getChannelList } from '@/api/baseRequest'
import {
  UNIT_TYPES,
  getUnitTypeName,
  getUnitTypeTagType,
  getUnitTypeColor,
  getCurrencyTypeName,
  getTaskTypeName,
  getTaskModeName,
  getTaskDurationName,
  getRankTypeName,
  getRankDurationName
} from '@/utils/unitTypeConstants'

export default {
  name: 'UnitManageDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    eventData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      dialogVisible: false,
      selectedUnit: null,
      taskUserId: '',
      poolType: '-1', // 奖池类型
      // 组件列表相关
      unitSearchText: '',
      unitTypeFilter: '',
      viewMode: 'grid', // 'grid' | 'list'
      currentPage: 1,
      pageSize: 12,
      // 常量数据
      UNIT_TYPES,
      taskConstants: [],
      taskModeConstants: [],
      taskDurationConstants: [],
      rankTypeConstants: [],
      rankDurationConstants: [],
      channelList: []
    }
  },
  computed: {
    filteredUnits() {
      if (!this.eventData.units) return []

      let units = this.eventData.units

      // 按类型筛选
      if (this.unitTypeFilter !== '') {
        units = units.filter(unit => unit.unitType === this.unitTypeFilter)
      }

      // 按搜索文本筛选
      if (this.unitSearchText) {
        const searchText = this.unitSearchText.toLowerCase()
        units = units.filter(unit =>
          (unit.unitName && unit.unitName.toLowerCase().includes(searchText)) ||
          (unit.unitAka && unit.unitAka.toLowerCase().includes(searchText)) ||
          (unit.unitDesc && unit.unitDesc.toLowerCase().includes(searchText)) ||
          unit.unitId.toString().includes(searchText)
        )
      }

      return units
    },
    paginatedUnits() {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.filteredUnits.slice(start, end)
    }
  },
  watch: {
    value(val) {
      this.dialogVisible = val
      if (val) {
        // 当对话框打开时获取常量数据
        this.getConstants()
        this.getChannelList()
      } else {
        this.resetData()
      }
    },
    dialogVisible(val) {
      this.$emit('input', val)
    }
  },
  methods: {
    getUnitTypeName(type) {
      return getUnitTypeName(type)
    },
    getUnitTypeColor(type) {
      return getUnitTypeColor(type)
    },
    getCurrencyTypeName(type) {
      return getCurrencyTypeName(type)
    },
    selectUnit(unit) {
      this.selectedUnit = unit
    },
    // 复制组件
    copyUnit(unit) {
      try {
        // 创建组件副本，排除ID和一些特定字段
        const copiedUnit = JSON.parse(JSON.stringify(unit))

        // 删除ID，让系统自动生成
        delete copiedUnit.unitId

        // 修改名称和别名，表示这是副本
        if (copiedUnit.unitName) {
          copiedUnit.unitName = `${copiedUnit.unitName}_copy`
        }
        if (copiedUnit.unitAka) {
          copiedUnit.unitAka = `${copiedUnit.unitAka}_副本`
        }

        // 清理和验证数据结构，确保导入时不会出错
        this.cleanupComponentData(copiedUnit)

        // 复制到剪贴板
        const componentData = JSON.stringify(copiedUnit, null, 2)
        this.copyToClipboard(componentData)

      } catch (error) {
        this.$message.error('复制组件失败: ' + error.message)
      }
    },
    // 清理组件数据，确保数据结构完整
    cleanupComponentData(componentData) {
      // 确保任务组件的必要字段存在
      if (componentData.taskUnit) {
        if (componentData.taskUnit.mode === undefined) {
          componentData.taskUnit.mode = 0
        }
        if (!componentData.taskUnit.config) {
          componentData.taskUnit.config = {
            checkParams: 0,
            showRate: 1,
            checkText: '',
            limit: -1,
            awardsKey: '',
            autoSendNotice: false,
            notice: ''
          }
        }
        if (!componentData.taskUnit.configs) {
          componentData.taskUnit.configs = []
        }
        if (!Array.isArray(componentData.taskUnit.checkParamsSet)) {
          componentData.taskUnit.checkParamsSet = []
        }
        // 新增：确保任务组件队伍相关字段存在
        if (componentData.taskUnit.teamTask === undefined) {
          componentData.taskUnit.teamTask = false
        }
        if (componentData.taskUnit.teamUnitId === undefined) {
          componentData.taskUnit.teamUnitId = 0
        }
      }

      // 确保排行组件的数组字段存在
      if (componentData.rankUnit) {
        if (!Array.isArray(componentData.rankUnit.checkParamsSet)) {
          componentData.rankUnit.checkParamsSet = []
        }
        if (!Array.isArray(componentData.rankUnit.rewardConfigs)) {
          componentData.rankUnit.rewardConfigs = []
        }
        // 新增：确保排行组件新字段存在
        if (componentData.rankUnit.enableRankLabel === undefined) {
          componentData.rankUnit.enableRankLabel = false
        }
        if (!Array.isArray(componentData.rankUnit.rankLabelConfigs)) {
          componentData.rankUnit.rankLabelConfigs = []
        }
        if (componentData.rankUnit.rankDuration === undefined) {
          componentData.rankUnit.rankDuration = 0
        }
        if (componentData.rankUnit.enablePool === undefined) {
          componentData.rankUnit.enablePool = false
        }
        if (!componentData.rankUnit.rankPoolInfo) {
          componentData.rankUnit.rankPoolInfo = {
            poolRate: 0,
            basePoolAmount: '',
            maxPoolAmount: '',
            showRate: 0,
            rankPoolConfigs: []
          }
        }
        if (!Array.isArray(componentData.rankUnit.rankPoolInfo.rankPoolConfigs)) {
          componentData.rankUnit.rankPoolInfo.rankPoolConfigs = []
        }
        // 新增：确保排行组件队伍相关字段存在
        if (componentData.rankUnit.teamRank === undefined) {
          componentData.rankUnit.teamRank = false
        }
        if (componentData.rankUnit.teamUnitId === undefined) {
          componentData.rankUnit.teamUnitId = 0
        }
        // 确保官方消息支持渠道字段存在
        if (!Array.isArray(componentData.rankUnit.noticeSupportChannel)) {
          componentData.rankUnit.noticeSupportChannel = []
        }
        // 确保子榜单配置字段存在
        if (componentData.rankUnit.enableSonRank === undefined) {
          componentData.rankUnit.enableSonRank = false
        }
        if (!componentData.rankUnit.sonRankConfig) {
          componentData.rankUnit.sonRankConfig = {
            showTop: 10,
            awardTop: 10,
            diffSex: false
          }
        }
      }

      // 确保步频组件的数组字段存在
      if (componentData.stepPoolUnit) {
        if (!Array.isArray(componentData.stepPoolUnit.costUnitIds)) {
          componentData.stepPoolUnit.costUnitIds = []
        }
        if (!Array.isArray(componentData.stepPoolUnit.sonPools)) {
          componentData.stepPoolUnit.sonPools = []
        }
        if (!Array.isArray(componentData.stepPoolUnit.stepConfigs)) {
          componentData.stepPoolUnit.stepConfigs = []
        }
        if (!Array.isArray(componentData.stepPoolUnit.stepRewards)) {
          componentData.stepPoolUnit.stepRewards = []
        }
      }

      // 确保商店组件的数组字段存在
      if (componentData.shopUnit) {
        if (!Array.isArray(componentData.shopUnit.costUnitIds)) {
          componentData.shopUnit.costUnitIds = []
        }
        if (componentData.shopUnit.duration === undefined) {
          componentData.shopUnit.duration = 0
        }
        if (!Array.isArray(componentData.shopUnit.items)) {
          componentData.shopUnit.items = []
        }
        if (componentData.shopUnit.items) {
          componentData.shopUnit.items.forEach(item => {
            if (item.globalLimit === undefined) {
              item.globalLimit = -1
            }
          })
        }
      }

      // 确保奖池组件的数组字段存在
      if (componentData.awardPoolUnit) {
        if (!Array.isArray(componentData.awardPoolUnit.costUnitId)) {
          componentData.awardPoolUnit.costUnitId = []
        }
        if (!Array.isArray(componentData.awardPoolUnit.costUnitIds)) {
          componentData.awardPoolUnit.costUnitIds = []
        }
        if (componentData.awardPoolUnit.enablePayPool === undefined) {
          componentData.awardPoolUnit.enablePayPool = false
        }
        if (componentData.awardPoolUnit.minPayUsd === undefined) {
          componentData.awardPoolUnit.minPayUsd = 0
        }
      }

      // 确保队伍组件的必要字段存在
      if (componentData.teamUnit) {
        if (componentData.teamUnit.maxMember === undefined) {
          componentData.teamUnit.maxMember = 0
        }
        if (componentData.teamUnit.duration === undefined) {
          componentData.teamUnit.duration = 0
        }
        // 新增：确保队伍组件新字段存在
        if (componentData.teamUnit.memberNotice === undefined) {
          componentData.teamUnit.memberNotice = ''
        }
        if (componentData.teamUnit.captainNotice === undefined) {
          componentData.teamUnit.captainNotice = ''
        }
      }
      
      // 确保货币合成组件的必要字段存在
      if (componentData.currencyMergeUnit) {
        if (componentData.currencyMergeUnit.duration === undefined) {
          componentData.currencyMergeUnit.duration = 0
        }
        if (componentData.currencyMergeUnit.limit === undefined) {
          componentData.currencyMergeUnit.limit = -1
        }
      }

      // 确保分享组件的必要字段存在
      if (componentData.shareUnit) {
        if (!Array.isArray(componentData.shareUnit.costUnitIds)) {
          componentData.shareUnit.costUnitIds = []
        }
        if (componentData.shareUnit.needCost === undefined) {
          componentData.shareUnit.needCost = 0
        }
        if (!componentData.shareUnit.shareAwardsKey) {
          componentData.shareUnit.shareAwardsKey = ''
        }
        if (!componentData.shareUnit.shareToAwardsKey) {
          componentData.shareUnit.shareToAwardsKey = ''
        }
        if (!componentData.shareUnit.shareToNotice) {
          componentData.shareUnit.shareToNotice = ''
        }
      }
      
      // 确保刮刮乐组件的必要字段存在
      if (componentData.scratchCardUnit) {
        if (componentData.scratchCardUnit.poolUnitId === undefined) {
          componentData.scratchCardUnit.poolUnitId = 0
        }
        if (!Array.isArray(componentData.scratchCardUnit.mappingList)) {
          componentData.scratchCardUnit.mappingList = []
        }
        // 确保mappingList中的每个对象都有必要的字段
        if (componentData.scratchCardUnit.mappingList) {
          componentData.scratchCardUnit.mappingList.forEach(mapping => {
            if (mapping.unitId === undefined) {
              mapping.unitId = null
            }
            if (!mapping.awardsKey) {
              mapping.awardsKey = ''
            }
          })
        }
        if (componentData.scratchCardUnit.lowPoolUnitId === undefined) {
          componentData.scratchCardUnit.lowPoolUnitId = 0
        }
        if (componentData.scratchCardUnit.randomMin === undefined) {
          componentData.scratchCardUnit.randomMin = 0
        }
        if (componentData.scratchCardUnit.randomMax === undefined) {
          componentData.scratchCardUnit.randomMax = 0
        }
      }
    },
    // 统一的剪贴板复制方法
    copyToClipboard(text) {
      // 优先使用现代 Clipboard API
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(() => {
          this.$message.success('组件数据已复制到剪贴板，可在新建组件时导入')
        }).catch((err) => {
          console.warn('Clipboard API 失败，使用降级方案:', err)
          this.copyToClipboardFallback(text)
        })
      } else {
        // 直接使用降级方案
        this.copyToClipboardFallback(text)
      }
    },
    // 剪贴板降级处理
    copyToClipboardFallback(text) {
      try {
        // 方法1: 使用 document.execCommand
        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        textArea.style.top = '-999999px'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()

        const successful = document.execCommand('copy')
        document.body.removeChild(textArea)

        if (successful) {
          this.$message.success('组件数据已复制到剪贴板，可在新建组件时导入')
          return
        }

        // 方法2: 如果 execCommand 失败，尝试使用 selection API
        this.copyToClipboardFallback2(text)

      } catch (err) {
        console.warn('execCommand 失败，尝试其他方法:', err)
        // 方法3: 最后的降级方案
        this.copyToClipboardFallback2(text)
      }
    },
    // 第二种降级方案
    copyToClipboardFallback2(text) {
      try {
        const range = document.createRange()
        const selection = window.getSelection()

        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        textArea.style.top = '-999999px'
        document.body.appendChild(textArea)

        range.selectNodeContents(textArea)
        selection.removeAllRanges()
        selection.addRange(range)
        textArea.setSelectionRange(0, textArea.value.length)

        const successful = document.execCommand('copy')
        document.body.removeChild(textArea)
        selection.removeAllRanges()

        if (successful) {
          this.$message.success('组件数据已复制到剪贴板，可在新建组件时导入')
        } else {
          this.showCopyManualDialog(text)
        }
      } catch (err) {
        console.warn('第二种降级方案也失败:', err)
        this.showCopyManualDialog(text)
      }
    },
    // 显示手动复制对话框
    showCopyManualDialog(text) {
      this.$alert(
        `<div style="text-align: left;">
          <p>自动复制失败，请手动复制以下内容：</p>
          <textarea style="width: 100%; height: 200px; margin-top: 10px; padding: 8px; border: 1px solid #ddd; border-radius: 4px; font-family: monospace; font-size: 12px;" readonly>${text}</textarea>
        </div>`,
        '手动复制',
        {
          dangerouslyUseHTMLString: true,
          confirmButtonText: '已复制',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).then(() => {
        this.$message.success('感谢您的配合！')
      }).catch(() => {
        this.$message.info('复制已取消')
      })
    },
    // 切换视图模式
    toggleViewMode() {
      this.viewMode = this.viewMode === 'grid' ? 'list' : 'grid'
    },
    // 分页处理
    handlePageChange(page) {
      this.currentPage = page
    },
    // 获取组件类型标签样式
    getUnitTypeTagType(type) {
      return getUnitTypeTagType(type)
    },
    // 获取表格行样式
    getRowClassName({ row }) {
      if (this.selectedUnit && this.selectedUnit.unitId === row.unitId) {
        return 'selected-row'
      }
      return ''
    },
    async refreshUserTask() {
      if (!this.taskUserId.trim()) {
        this.$message.warning('请输入用户RID')
        return
      }

      if (!this.selectedUnit) {
        this.$message.warning('请先选择任务组件')
        return
      }

      try {
        const res = await refreshUnitTaskReq({
          eventCode: this.eventData.eventCode,
          unitId: this.selectedUnit.unitId,
          rid: parseInt(this.taskUserId)
        })

        if (res.includes('success') || res.includes('成功')) {
          this.$message.success('用户任务刷新成功')
          this.taskUserId = ''
        } else {
          this.$message.error('用户任务刷新失败: ' + res)
        }
      } catch (error) {
        this.$message.error('刷新用户任务失败')
      }
    },
    async refreshAwardPool() {
      if (!this.selectedUnit) {
        this.$message.warning('请先选择奖池组件')
        return
      }

      if (!this.poolType) {
        this.$message.warning('请选择奖池类型')
        return
      }

      this.$confirm('刷新奖池会重新生成奖池内容，确定要继续吗？', '确认刷新', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await refreshUnitAwardPoolReq({
            eventCode: this.eventData.eventCode,
            unitId: this.selectedUnit.unitId,
            poolType: this.poolType
          })

          if (res.code === 0 && res.data === true) {
            this.$message.success('奖池刷新成功')
          } else {
            this.$message.error('奖池刷新失败: ' + (res.msg || '未知错误'))
          }
        } catch (error) {
          this.$message.error('刷新奖池失败')
        }
      })
    },
    openRankManage() {
      // 直接打开排行榜管理
      this.$emit('open-rank-dialog', this.eventData)
      this.handleClose()
    },
    resetData() {
      this.selectedUnit = null
      this.taskUserId = ''
      this.poolType = '-1'
      this.unitSearchText = ''
      this.unitTypeFilter = ''
      this.currentPage = 1
    },
    handleClose() {
      this.dialogVisible = false
    },

    // 图片处理相关方法
    handleImageError(event) {
      // 图片加载失败时的处理
      event.target.style.display = 'none'
      event.target.nextElementSibling.style.display = 'block'
    },
    handleImageLoad(event) {
      // 图片加载成功时的处理
      event.target.style.display = 'block'
      const noImageElement = event.target.nextElementSibling
      if (noImageElement) {
        noImageElement.style.display = 'none'
      }
    },

    // 获取常量数据
    async getConstants() {
      try {
        const { getConstList } = await import('@/api/constRequest')

        const [taskTypeRes, taskModeRes, taskDurationRes, rankTypeRes, rankDurationRes] = await Promise.all([
          getConstList('taskType'),
          getConstList('taskMode'),
          getConstList('taskDuration'),
          getConstList('activityRankType'),
          getConstList('rankCycleType')
        ])

        if (taskTypeRes.code === 0) this.taskConstants = taskTypeRes.data
        if (taskModeRes.code === 0) this.taskModeConstants = taskModeRes.data
        if (taskDurationRes.code === 0) this.taskDurationConstants = taskDurationRes.data
        if (rankTypeRes.code === 0) this.rankTypeConstants = rankTypeRes.data
        if (rankDurationRes.code === 0) this.rankDurationConstants = rankDurationRes.data

      } catch (error) {
        console.error('获取常量数据失败:', error)
      }
    },
    
    // 获取渠道列表数据
    async getChannelList() {
      try {
        const channelData = await getChannelList()
        this.channelList = channelData
      } catch (error) {
        console.error('获取渠道列表数据失败:', error)
        // 如果获取失败，使用默认数据
        this.channelList = [
          { value: 'cde', name: 'CDE' },
          { value: 'googleplay', name: 'Google Play' },
          { value: 'appstore', name: 'App Store' },
          { value: 'web', name: 'Web' }
        ]
      }
    },
    
    // 根据渠道值获取渠道名称
    getChannelName(channelValue) {
      const channel = this.channelList.find(c => c.value === channelValue)
      return channel ? (channel.name || channel.label) : channelValue
    },
    getTaskTypeName(type) {
      return getTaskTypeName(type, this.taskConstants)
    },
    getTaskModeName(mode) {
      return getTaskModeName(mode, this.taskModeConstants)
    },
    getTaskDurationName(duration) {
      return getTaskDurationName(duration, this.taskDurationConstants)
    },
    getRankTypeName(type) {
      return getRankTypeName(type, this.rankTypeConstants)
    },
    getRankDurationName(duration) {
      return getRankDurationName(duration, this.rankDurationConstants)
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return 'N/A'
      // 如果时间戳是秒级的，需要转换为毫秒级
      const milliseconds = timestamp < 10000000000 ? timestamp * 1000 : timestamp
      const date = new Date(milliseconds)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    },

    // 获取货币类型名称
    getCurrencyTypeName(type) {
      return getCurrencyTypeName(type)
    },
    
    // 格式化排名范围显示
    formatRankRange(configs, index) {
      // 确保配置按minTop排序
      const sortedConfigs = [...configs].sort((a, b) => a.minTop - b.minTop)
      const current = sortedConfigs[index]
      const next = index < sortedConfigs.length - 1 ? sortedConfigs[index + 1] : null
      
      // 如果是最后一个配置，显示到TOP值
      if (!next) {
        const topValue = this.selectedUnit?.rankUnit?.showTop || 50
        if (current.minTop < topValue) {
          return `第${current.minTop}-${topValue}名`
        }
        return `第${current.minTop}名`
      }
      
      // 如果有下一个配置，显示到下一个配置的前一名
      if (current.minTop < next.minTop - 1) {
        return `第${current.minTop}-${next.minTop - 1}名`
      }
      return `第${current.minTop}名`
    }
  }
}
</script>

<style scoped lang="scss">
:deep(.el-alert__title) {
  font-size: 20px;
}

.unit-manage-content {
  .units-overview {
    margin-bottom: 30px;

    .overview-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;

      h3 {
        margin: 0;
        color: #303133;
      }

      .overview-controls {
        display: flex;
        gap: 12px;
        align-items: center;
      }
    }

    .units-stats {
      margin-bottom: 16px;
    }

    .units-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
      gap: 16px;

      .unit-card {
        padding: 16px;
        border: 1px solid #e4e7ed;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.3s;

        &:hover {
          border-color: #409eff;
          box-shadow: 0 2px 8px rgba(64, 158, 255, 0.2);
        }

        &.selected {
          border-color: #409eff;
          background-color: #f0f9ff;
        }

        .unit-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;

          .unit-type {
            color: white;
            padding: 2px 8px;
            border-radius: 4px;
            font-size: 12px;
          }

          .unit-id {
            color: #909399;
            font-size: 12px;
          }
        }

        .unit-title {
          font-weight: bold;
          margin-bottom: 4px;
          color: #303133;
        }

        .unit-desc {
          color: #666;
          font-size: 12px;
          margin-bottom: 4px;
        }

        .unit-weight {
          color: #909399;
          font-size: 11px;
        }

        .unit-actions {
          margin-top: 8px;
          display: flex;
          justify-content: flex-end;

          .el-button {
            margin: 0;
          }
        }
      }
    }
  }

  .unit-operations {
    h3 {
      margin-bottom: 16px;
      color: #303133;
    }

    .task-operations,
    .pool-operations,
    .rank-operations,
    .currency-info,
    .shop-info,
    .step-pool-info,
    .team-info,
    .share-info,
    .scratch-card-info {
      margin-bottom: 20px;

      .operation-form {
        margin-top: 12px;

        .operation-desc {
          margin-top: 8px;
          color: #666;
          font-size: 12px;
        }
      }

      .currency-details,
      .shop-details,
      .pool-details,
      .rank-details,
      .task-details,
      .step-pool-details,
      .team-details,
      .share-details,
      .scratch-card-details {
        margin-top: 12px;

        .detail-section {
          margin-top: 16px;

          h5 {
            margin-bottom: 8px;
            color: #606266;
            font-size: 14px;
            font-weight: 600;
          }

          .el-table {
            margin-top: 8px;
          }

          .el-tag {
            margin-right: 8px;
            margin-bottom: 4px;
          }
        }
      }
    }

    .unit-details {
      margin-top: 20px;
      padding-top: 20px;
      border-top: 1px solid #e4e7ed;

      h4 {
        margin-bottom: 12px;
        color: #303133;
      }
    }

    // 列表视图样式
    .units-list {
      ::v-deep .selected-row {
        background-color: #ecf5ff !important;

        &:hover {
          background-color: #d9ecff !important;
        }
      }
    }

    // 分页样式
    .units-pagination {
      margin-top: 20px;
      text-align: center;
    }
  }

  .no-selection {
    text-align: center;
    padding: 60px 0;
  }
}

.dialog-footer {
  text-align: right;
}

/* 积分图标展示样式 */
.score-icon-display {
  display: flex;
  align-items: center;
  gap: 16px;

  .icon-preview {
    position: relative;
    width: 60px;
    height: 60px;
    border: 1px solid #e4e7ed;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f8f9fa;

    .score-icon-img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      border-radius: 2px;
    }

    .no-image {
      color: #909399;
      font-size: 12px;
      text-align: center;
    }
  }

  .icon-link {
    flex: 1;
  }
}

/* 货币图标URL展示样式 */
.icon-url-display {
  display: flex;
  align-items: center;
  gap: 16px;

  .icon-preview {
    position: relative;
    width: 60px;
    height: 60px;
    border: 2px solid #e4e7ed;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;

    &:hover {
      border-color: #409eff;
      box-shadow: 0 4px 12px rgba(64, 158, 255, 0.2);
      transform: translateY(-2px);
    }

    .currency-icon {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      border-radius: 4px;
      transition: transform 0.3s ease;

      &:hover {
        transform: scale(1.05);
      }
    }

    .no-icon {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      color: #909399;
      font-size: 12px;
      text-align: center;

      i {
        font-size: 20px;
        color: #c0c4cc;
      }
    }
  }

  .icon-link {
    flex: 1;
    display: flex;
    align-items: center;

    .el-link {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      border-radius: 6px;
      transition: all 0.3s ease;

      &:hover {
        background-color: #ecf5ff;
        transform: translateX(2px);
      }

      i {
        font-size: 14px;
      }
    }

    .no-url {
      color: #c0c4cc;
      font-style: italic;
    }
  }
}

/* 队伍头像展示样式 */
.team-avatar-display {
  display: flex;
  align-items: center;
  gap: 16px;

  .avatar-preview {
    position: relative;
    width: 60px;
    height: 60px;
    border: 2px solid #e4e7ed;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;

    &:hover {
      border-color: #409eff;
      box-shadow: 0 4px 12px rgba(64, 158, 255, 0.2);
      transform: translateY(-2px);
    }

    .team-avatar {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      border-radius: 4px;
      transition: transform 0.3s ease;

      &:hover {
        transform: scale(1.05);
      }
    }

    .no-avatar {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      color: #909399;
      font-size: 12px;
      text-align: center;

      i {
        font-size: 20px;
        color: #c0c4cc;
      }
    }
  }

  .avatar-link {
    flex: 1;
    display: flex;
    align-items: center;

    .el-link {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      border-radius: 6px;
      transition: all 0.3s ease;

      &:hover {
        background-color: #ecf5ff;
        transform: translateX(2px);
      }

      i {
        font-size: 14px;
      }
    }

    .no-url {
      color: #c0c4cc;
      font-style: italic;
    }
  }
}

/* 队员头像样式 */
.member-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid #e4e7ed;
}

/* 通知消息样式 */
.notice-message {
  max-width: 100%;
  padding: 8px 12px;
  background-color: #f8f9fa;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.5;
  color: #606266;
  word-wrap: break-word;
  white-space: pre-wrap;
  
  &:empty::before {
    content: '未设置';
    color: #c0c4cc;
    font-style: italic;
  }
}
</style>